import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  OutlinedInput,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import financialConfigService from '../../services/financialConfigService';
import { getQuickBooksEntities } from '../../services/quickbooksService';
import brandService from '../../services/brandService';

export default function BrandRulesManager() {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rules, setRules] = useState([]);
  const [qbEntities, setQbEntities] = useState([]);
  const [brands, setBrands] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    qb_connector_id: '',
    currency_filter: '',
    geography_filter: '',
    brand_filter: [],
    priority: 1,
    is_active: 1
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all mapping rules
      try {
        const rulesRes = await financialConfigService.getBrandRules();
        if (rulesRes?.success && rulesRes?.data) {
          setRules(rulesRes.data);
        }
      } catch (err) {
        console.error('Error loading mapping rules:', err);
        setRules([]);
      }

      // Load QB entities
      try {
        const entitiesRes = await getQuickBooksEntities();
        if (entitiesRes?.success && entitiesRes?.data) {
          setQbEntities(entitiesRes.data);
        }
      } catch (err) {
        console.error('Error loading QB entities:', err);
        setQbEntities([]);
      }

      // Load all brands
      try {
        const brandsRes = await brandService.getBrands();
        if (Array.isArray(brandsRes)) {
          setBrands(brandsRes);
        }
      } catch (err) {
        console.error('Error loading brands:', err);
        setBrands([]);
      }

    } catch (err) {
      console.error('Error loading mapping rules data:', err);
      setError('Failed to load mapping rules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);

      // Parse brand_filter from comma-separated string to array
      let brandFilterArray = [];
      if (rule.brand_filter) {
        brandFilterArray = rule.brand_filter.split(',').map(id => parseInt(id.trim()));
      }

      setFormData({
        qb_connector_id: rule.qb_connector_id,
        currency_filter: rule.currency_filter || '',
        geography_filter: rule.geography_filter || '',
        brand_filter: brandFilterArray,
        priority: rule.priority,
        is_active: rule.is_active
      });
    } else {
      setEditingRule(null);
      setFormData({
        qb_connector_id: '',
        currency_filter: '',
        geography_filter: '',
        brand_filter: [],
        priority: rules.length + 1,
        is_active: 1
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.qb_connector_id) {
        alert('Please select a QuickBooks Company');
        return;
      }

      // Convert brand_filter array to comma-separated string
      const submitData = {
        ...formData,
        brand_filter: formData.brand_filter.length > 0
          ? formData.brand_filter.join(',')
          : null
      };

      if (editingRule) {
        await financialConfigService.updateBrandRule(editingRule.id, submitData);
      } else {
        await financialConfigService.createBrandRule(submitData);
      }

      loadData();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving mapping rule:', err);
      alert('Failed to save mapping rule');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'Delete Rule', message: 'Are you sure you want to delete this mapping rule?', confirmText: 'Delete', severity: 'danger' })) {
      return;
    }

    try {
      await financialConfigService.deleteBrandRule(id);
      loadData();
    } catch (err) {
      console.error('Error deleting mapping rule:', err);
      alert('Failed to delete mapping rule');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const getQBEntityName = (connectorId) => {
    const entity = qbEntities.find(e => e.connector_id === connectorId);
    return entity ? entity.company_name : 'Unknown';
  };

  const getBrandNames = (brandFilterString) => {
    if (!brandFilterString) return 'All Brands';

    const brandIds = brandFilterString.split(',').map(id => parseInt(id.trim()));
    const brandNames = brandIds
      .map(id => {
        const brand = brands.find(b => b.id === id);
        return brand ? brand.name : `Brand ${id}`;
      })
      .join(', ');

    return brandNames;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            QuickBooks Mapping Rules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure rules to automatically map stores to QuickBooks companies based on currency, geography, and brand
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Rule
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          How Mapping Rules Work:
        </Typography>
        <Typography variant="body2">
          • Rules are evaluated in priority order (lower number = higher priority)
          <br />
          • Each rule defines a QB company and optional filters (currency, geography, brand)
          <br />
          • The first rule that matches all specified filters will be used
          <br />
          • Leave filters empty to create catch-all rules that match any value
          <br />
          • Example: Priority 1 rule for "USD + US" will override Priority 2 rule for "USD only"
        </Typography>
      </Alert>

      {/* Rules Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Priority</TableCell>
                  <TableCell>QuickBooks Company</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Geography</TableCell>
                  <TableCell>Brands</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No mapping rules configured yet. Click "Add Rule" to create your first rule.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <Chip
                          label={rule.priority}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {getQBEntityName(rule.qb_connector_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {rule.currency_filter ? (
                          <Chip label={rule.currency_filter} size="small" color="info" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">All</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {rule.geography_filter ? (
                          <Chip label={rule.geography_filter} size="small" color="success" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">All</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {rule.brand_filter ? (
                          <Tooltip title={getBrandNames(rule.brand_filter)}>
                            <Chip
                              label={getBrandNames(rule.brand_filter)}
                              size="small"
                              color="warning"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">All Brands</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rule.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={rule.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(rule)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(rule.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRule ? 'Edit Mapping Rule' : 'Add Mapping Rule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>QuickBooks Company *</InputLabel>
              <Select
                value={formData.qb_connector_id}
                label="QuickBooks Company *"
                onChange={(e) => setFormData({ ...formData, qb_connector_id: e.target.value })}
              >
                {qbEntities.map((entity) => (
                  <MenuItem key={entity.connector_id} value={entity.connector_id}>
                    {entity.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Currency Filter"
              value={formData.currency_filter}
              onChange={(e) => setFormData({ ...formData, currency_filter: e.target.value })}
              placeholder="e.g., USD,CAD,EUR"
              helperText="Comma-separated list. Leave empty to match all currencies."
            />

            <TextField
              fullWidth
              label="Geography Filter"
              value={formData.geography_filter}
              onChange={(e) => setFormData({ ...formData, geography_filter: e.target.value })}
              placeholder="e.g., US,CA,MX"
              helperText="Comma-separated list. Leave empty to match all geographies."
            />

            <FormControl fullWidth>
              <InputLabel>Brand Filter (Optional)</InputLabel>
              <Select
                multiple
                value={formData.brand_filter}
                onChange={(e) => setFormData({ ...formData, brand_filter: e.target.value })}
                input={<OutlinedInput label="Brand Filter (Optional)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((brandId) => {
                      const brand = brands.find(b => b.id === brandId);
                      return (
                        <Chip
                          key={brandId}
                          label={brand ? brand.name : `Brand ${brandId}`}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {brands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Leave empty to apply this rule to all brands. Select specific brands for exceptions.
              </Typography>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Priority *"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              helperText="Lower number = higher priority (evaluated first)"
              inputProps={{ min: 1 }}
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.is_active}
                label="Status"
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.qb_connector_id || !formData.priority}
          >
            {editingRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
